import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';
import { IChartDatasets, IChartViewModel } from 'src/app/share/components/card-chart/card-chart.component';
import { ITableViewModel } from 'src/app/share/entities/view-model';
import { FormInputComponent, IFormInputViewModel } from 'src/app/share/forms/form-input/form-input.component';
import { FormSelectComponent, IDisplaySelectViewModel, IFormSelectChangedArg, IFormSelectViewModel } from 'src/app/share/forms/form-select/form-select.component';
import { IPageDataViewModel } from 'src/app/share/pages/pages';
import { CommonService } from 'src/app/share/services/common.service';
import { DateUtil } from 'src/app/share/utils/date-util';
import { StringUtil } from 'src/app/share/utils/string-util';
import { ReportService } from '../services/report.service';

@Component({
  selector: 'app-trend-dashboard',
  templateUrl: './trend-dashboard.component.html',
  styleUrls: ['./trend-dashboard.component.scss']
})
export class TrendDashboardComponent implements OnInit {

  constructor(private commonService: CommonService, private router: Router, private reportService: ReportService) { }

  vm$: Observable<IViewModel>;

  private pageCommandBs$: BehaviorSubject<string> = new BehaviorSubject<string>('initialize');

  reportComponentUrl: string = 'trend-report/';

  ngOnInit(): void {

    var today = new Date();
    
    const loader$: Observable<IViewModel> = of({
      filterData:  {
        fromDate: moment(new Date(today.getFullYear(), 0, 1)).format('YYYY-MM-DD'),
        toDate: moment(DateUtil.getLastDayOfPreviousMonth(today)).format('YYYY-MM-DD'),
        completedOnly: false,
        readyForBilling: false,
        yearToCompare: today.getFullYear()-1
      }
    }).pipe(
      switchMap(ctx => this.loadDashboardVm$(ctx.filterData).pipe(
        map((rvm: IDashboardViewModel) => {
          return {
            ...ctx,
            dashboardVm: rvm
          };
        })
      )),
      map(ctx => { 

        const ret: IViewModel = {
          actionsAllowed: true, canExportExcel: false,
          filterVm: {
            data: ctx.filterData,
            fromDateInputVm: FormInputComponent.viewModel(ctx.filterData, 'fromDate',
                      { title: 'From Date', isRequired: false, type: 'date' }),
            toDateInputVm: FormInputComponent.viewModel(ctx.filterData, 'toDate',
                      { title: 'To Date', isRequired: false, type: 'date' }),
            yearToCompareVm: FormSelectComponent.viewModel(ctx.filterData, 'yearToCompare',
                      { title: 'Year to Compare', isRequired: false, type: 'select' }, this.reportService.setYearToCompare())
          },
          dashboardVm: ctx.dashboardVm
        }
        return ret;
      })
    );

    // commands
    const comand$: Observable<IViewModel> = this.pageCommandBs$.pipe(
      switchMap(cmdText => { 
        const ret: IViewModel = {
          actionsAllowed: false, canExportExcel: false, 
          filterVm: undefined,
          dashboardVm: {
            workingText: cmdText == 'initialize' ? 'Initializing...' : cmdText == 'refresh' ? 'Refreshing...' : 'Loading...',
            errorText: undefined
          }
        };

        if (cmdText == 'refresh')
          return merge(of(ret), loader$);
        else
          return of(ret);
      })
    );

    this.vm$ = merge(comand$, loader$);
  }

  /**
   * 
   */
  private loadDashboardVm$(filterData: IDashboardFilterData): Observable<IDashboardViewModel> {
    return of({}).pipe(
      switchMap(ctx => this.commonService.getData('PageCommand', 'trending.report', 'page-load', filterData, '/api/SantaCruz', true)),
      map((v: IDashboardViewModel) => {
        if (v.workingText)
          return { ...v };
        const ret = { ...v, actionsAllowed: true };
        if (ret.data) {

            ret.tabVms = [];

            if (ret.data.parentGroup) {
              ret.tabVms.push({
                active: true,
                title: 'Parent Group',
                chartVm: {
                  ...ret.data.parentGroup,
                  revenueVm: this.chartViewModelVm(ret.data.parentGroup.revenue, false, 'parent-group-trending-report', 'Revenue by Parent Group', 'currency', 'bar', true, true),
                  tripsCountVm: this.chartViewModelVm(ret.data.parentGroup.tripsCount, false, 'parent-group-trending-report', 'Trip Count by Parent Group', 'number', 'bar', true, true),
                  averageTicketPriceVm: this.chartViewModelVm(ret.data.parentGroup.averageTicketPrice, false, 'parent-group-trending-report', 'Average Ticket Price by Parent Group', 'currency', 'bar', true, true),
                  averageTripDistanceVm: this.chartViewModelVm(ret.data.parentGroup.averageTripDistance, false, 'parent-group-trending-report', 'Average Trip Distance by Parent Group', 'number', 'bar', true, true),
                  averageTimeVm: this.chartViewModelVm(ret.data.parentGroup.averageTime, false, 'parent-group-trending-report', 'Average Time by Parent Group', 'number', 'bar', true, true)
                }
              })
            }

            if (ret.data.vehicle) {
              ret.tabVms.push({
                active: false,
                title: 'Vehicle Type',
                chartVm: {
                  ...ret.data.vehicle,
                  revenueVm: this.chartViewModelVm(ret.data.vehicle.revenue, false, 'revenue-by-vehicle-type', 'Revenue by Vehicle Type', 'currency', 'bar', true, true),
                  tripsCountVm: this.chartViewModelVm(ret.data.vehicle.tripsCount, false, 'revenue-by-vehicle-type', 'Count by Vehicle Type', 'number', 'bar', true, true),
                  averageTicketPriceVm: this.chartViewModelVm(ret.data.vehicle.averageTicketPrice, false, 'revenue-by-vehicle-type', 'Average Ticket Price by Vehicle Type', 'currency', 'bar', true, true)
                }
              })
            }
          

            if (ret.data.serviceArea) {
              ret.tabVms.push({
                active: false,
                title: 'Service Area',
                chartVm: {
                  ...ret.data.serviceArea,
                  revenueVm: this.chartViewModelVm(ret.data.serviceArea.revenue, false, 'revenue-by-market', 'Revenue by Service Area', 'currency', 'bar', true, true),
                  tripsCountVm: this.chartViewModelVm(ret.data.serviceArea.tripsCount, false, 'revenue-by-market', 'Trip Count by Service Area', 'number', 'bar', true, true),
                  averageTicketPriceVm: this.chartViewModelVm(ret.data.serviceArea.averageTicketPrice, false, 'revenue-by-market', 'Average Ticket Price by Service Area', 'currency', 'bar', true, true),
                  averageTripDistanceVm: this.chartViewModelVm(ret.data.serviceArea.averageTripDistance, false, 'revenue-by-market', 'Average Trip Distance by Service Area', 'number', 'bar', true, true),
                  averageTimeVm: this.chartViewModelVm(ret.data.serviceArea.averageTime, false, 'revenue-by-market', 'Average Time by Service Area', 'number', 'bar', true, true)
                }
              })
            }

              if (ret.data.tripType) {
                ret.tabVms.push({
                  active: false,
                  title: 'Trip Type',
                  chartVm: {
                    ...ret.data.tripType,
                    revenueVm: this.chartViewModelVm(ret.data.tripType.revenue, false, 'revenue-per-trip-type', 'Revenue by Trip Type', 'currency', 'bar', true, true),
                    tripsCountVm: this.chartViewModelVm(ret.data.tripType.tripsCount, false, 'revenue-per-trip-type', 'Trip Count by Trip Type', 'number', 'bar', true, true),
                    averageTicketPriceVm: this.chartViewModelVm(ret.data.tripType.averageTicketPrice, false, 'revenue-per-trip-type', 'Average Ticket Price by Trip Type', 'currency', 'bar', true, true),
                    averageTimeVm: this.chartViewModelVm(ret.data.tripType.averageTime, false, 'revenue-per-trip-type', 'Average Time by Trip Type', 'number', 'bar', true, true)
                  }
                })
              }

            ret.lastDateTimeUpdated = new Date().toLocaleDateString();  
          }
        return ret;
      })
    );
  }

  /**
   * 
   */
  private chartViewModelVm(data: IViewData, formatNumber: boolean, type: string, title: string, numberType: string = 'currency',
    chartType: string = 'pie', legend: boolean = true, isDetailsAvailable: boolean = true): IChartViewModel {

    if (data == undefined || data?.dataList?.length == 0)
      return undefined;

    const chartOptions: any = {
      responsive: true,
      legend: {
        position: 'top',
        labels: {
          // This more specific font property overrides the global property
          fontColor: 'blue',
          fontWeight: 'bold',
        } 
      },
      scales: {
        yAxes: [
          {
            gridLines: {
              display: true,
              color: "rgba(0, 0, 0, 0.1)"
            },
            ticks: {
              userCallback: function (tick: any) {
                return numberType == 'currency' ? StringUtil.toCurrency(tick) : StringUtil.toNumber(tick);
              }
            }
          }
        ]
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return numberType == 'currency' ? StringUtil.toCurrency(context.parsed.y) : StringUtil.toNumber(context.parsed.y);
          }
        }
      },
      plugins: {
        plugins: {
          tooltip: {
            callbacks: {
              label: function (context) {
                return numberType == 'currency' ?  StringUtil.toCurrency(context.parsed.y) : StringUtil.toNumber(context.parsed.y);
              }
            }
          }
        }
      },
    }

    var chartDatasets: IChartDatasets[] = [];
    var chartLabels: any[] = [];
    var chartColors: any[] = [];
    if (data?.dataList[0]?.datasets) {
      chartDatasets.push({ label: data?.dataList[0]?.label ?? '', data: data?.dataList[0]?.datasets?.map(d => formatNumber ? StringUtil.parseAndFormatNumber((d.dataset ?? '')) : d.dataset ?? '') });
      chartLabels.push( { data: data?.dataList[0]?.datasets?.map(d => d.label ?? '') });
      chartColors.push(
        {
          backgroundColor: data?.dataList[0]?.datasets.map(b => b.color.backgroundColor ?? ''),
          hoverBackgroundColor: data?.dataList[0]?.datasets.map(b => b.color.hoverBackgroundColor ?? ''),
          borderWidth: Math.max(...data?.dataList[0]?.datasets.map(b => b.color.borderWidth ?? 2))
        }
      );
    }
    if (data?.dataList[1]?.datasets) {
      chartDatasets.push({ label: data?.dataList[1]?.label ?? '', data: data?.dataList[1]?.datasets?.map(d => formatNumber ? StringUtil.parseAndFormatNumber((d.dataset ?? '')) : d.dataset ?? '') });
      chartColors.push(
        {
          backgroundColor: data?.dataList[1]?.datasets.map(b => b.color.backgroundColor ?? ''),
          hoverBackgroundColor: data?.dataList[1]?.datasets.map(b => b.color.hoverBackgroundColor ?? ''),
          borderWidth: Math.max(...data?.dataList[1]?.datasets.map(b => b.color.borderWidth ?? 2))
        }
      );
    }

    const ret: IChartViewModel = {
      label: data?.label ?? '',
      chartType: chartType,
      chartOptions: chartOptions,
      chartDatasets: chartDatasets,
      chartLabels: data?.dataList[0]?.datasets?.map(d => d.label ?? ''),
      chartColors: chartColors,
      title: title,
      legend: legend,
      clientRoute: this.reportComponentUrl + type,
      isDetailsAvailable: isDetailsAvailable,
      chartClick: this.chartVm_clicked,
      chartHover: this.chartVm_hovered
    };

    return ret;
  }

  /**
   * 
   */
  refreshPage() {
    this.pageCommandBs$.next('refresh');
  }

  /**
   * 
   * @param pvm 
   */
  async doSearch(pvm: IViewModel) {
    try {
      pvm.dashboardVm.workingText = 'Filtering...';
      pvm.dashboardVm.errorText = undefined;

      var result = await of({}).pipe(
        delay(500),
        switchMap(ctx => this.loadDashboardVm$(pvm.filterVm.data))
      ).toPromise();

      pvm.dashboardVm = result;

    } catch (err) {
      pvm.dashboardVm.errorText = err;
    } finally {
      pvm.dashboardVm.workingText = undefined;
    }
  }

  viewDetailsClicked(url: string, line: string): void {
    this.router.navigateByUrl(url + '/' + line);
  }

  chartVm_clicked(vm: IViewModel, cvm: IChartViewModel, event: any): void {
  }


  chartVm_hovered(vm: IViewModel, cvm: IChartViewModel, event: any): void {
  }

  /**
   * 
   * @param vm 
   * @param fvm 
   */
  requestFormValidate(vm: IViewModel) {
    vm.isValid = true;
  }

  yearToCompareChanged(e: IFormSelectChangedArg, vm: IViewModel) {

  }

  /**
   * 
   * @param event 
   */
  onGetActiveTab(pvm: IViewModel, event: any) { 
    const title = event?.el?.heading?.length ? event.el.heading : '';

    pvm.dashboardVm.tabVms.forEach(tvm => tvm.active = tvm.title == title);
  }
}


/**
 * 
 */
interface IViewModel extends IPageDataViewModel {
  dashboardVm?: IDashboardViewModel;

  actionsAllowed?: boolean;

  filterVm?: {
    data: IDashboardFilterData;
    fromDateInputVm?: IFormInputViewModel;
    toDateInputVm?: IFormInputViewModel;
    yearToCompareVm?: IFormSelectViewModel;
  }

  isValid?: boolean;
}

/**
 * 
 */
interface IDashboardFilterData {
  fromDate?: string;
  toDate?: string;
  completedOnly?: boolean;
  readyForBilling?: boolean;
  yearToCompare?: number;
}

/**
 * 
 */
interface IDashboardViewModel {
  data?: { 
    parentGroup: IDashboardDataViewModel, vehicle: IDashboardDataViewModel, serviceArea: IDashboardDataViewModel, tripType: IDashboardDataViewModel
  };

  
  tabVms?: IReportTabViewModel[];

  lastDateTimeUpdated?: string;
  workingText?: string;
  errorText?: string;
}

interface IDashboardDataViewModel {
  revenue?: IViewData;
  tripsCount?: IViewData;
  averageTicketPrice?: IViewData;
  averageTripDistance?: IViewData;
  averageTime?: IViewData
}

/**
 * 
 */
interface IViewData {
  label?: string;
  dataList?: IChartDataSet[];
}

/**
 * 
 */
interface IChartDataSet {
  label?: string;
  datasets?: IDataModel[];
}

/**
 * 
 */
interface IDataModel {
  dataset?: string;
  label?: string;
  color?: IChartColor;
}

/**
 * 
 */
interface IChartColor {
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  borderWidth?: number;
}

/**
 * 
 */
interface IReportTabViewModel {
  title: string;
  chartVm: IReportTabDataViewModel;
  active: boolean;
}

interface IReportTabDataViewModel {
  data?: { 
    revenue: IViewData, tripsCount: IViewData, averageTicketPrice: IViewData, averageTripDistance: IViewData, averageTime: IViewData
  };

  revenueVm?: IChartViewModel;
  tripsCountVm?: IChartViewModel;
  averageTicketPriceVm?: IChartViewModel;
  averageTripDistanceVm?: IChartViewModel;
  averageTimeVm?: IChartViewModel;
}